import useAppStore from "@/context/store";
import { filterBoards } from "@/utils/filterBoards";
import sortBoardsByTitleAndCreateTime, {
  setBoardsTitlePinyin,
} from "@/utils/sortBoardsByTitleAndCreateTime";
import useRequest from "@/utils/useReq";
import { useMount } from "ahooks";
import { Select } from "antd";
import classNames from "classnames";
import _ from "lodash";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Else, If, Then, When } from "react-if";
import CreateBoard from "../CreateBoard";
import HBIcon from "../HBIcon";
import HBLoading from "../HBLoading";
import BoardSelectBoardList from "./BoardSelectBoardList";
import styles from "./index.module.less";
const LAST_BOARD_NUM = 5;

function BoardSelect(
  {
    value: _value,
    onChange,
    placeholder,
    useLabel,
    useCreation,
    onlyCreation,
    selectFirst,
    // 该字段主要用于插入需要展示，但是不可以选择的画板，重复的画板ID会被去重，主要用于待归类采集画板
    appendBoardList,
    emptyBoardsExpand = true,
    ...props
  },
  ref
) {
  const [value, setValue] = useState(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const menuRef = useRef(null);
  const defaultSelectedRef = useRef(false);
  const { lastBoards: userBoards, setLastBoards } = useAppStore(
    ({ lastBoards, setLastBoards }) => ({ lastBoards, setLastBoards })
  );
  const userBoardsReady = useAppStore((s) => s.lastBoardsReady);

  const { data: boardData, loading: boardLoading } = useRequest(
    async () => {
      // const result = await getLastBoards();
      // 将画板增加拼音
      let boardList = userBoards;
      if (boardList?.length > 0 && !boardList?.[0]?._pinyin) {
        boardList = await setBoardsTitlePinyin(boardList);
        setLastBoards(boardList);
        return;
      }
      // 过滤原创画板
      let boards = boardList;
      if (!useCreation) {
        boards = boardList?.filter((board) => !board?.extra?.is_creation) || [];
      } else if (onlyCreation) {
        boards = boardList?.filter((board) => board?.extra?.is_creation) || [];
      }
      // 为画板添加首字母与拼音
      const withPinyinBoards = boards.map((board) => {
        const item = { ...board };
        let firstLetter = `${
          item._pinyin[0].length > 0 ? item._pinyin[0][0] : item._pinyin[0]
        }`.toLocaleUpperCase();
        if (!/[A-Z]/.test(firstLetter)) {
          firstLetter = "#";
        }
        item._firstLetter = firstLetter;
        return item;
      });
      // 前5个画板作为历史画板
      const lastBoards = withPinyinBoards
        .slice(0, LAST_BOARD_NUM)
        .map((board, index) => {
          const item = {
            ...board,
          };
          if (index === 0) {
            item._icon = "time";
          }
          return item;
        });
      // 剩余画板按字母排序
      const otherBoards = Object.entries(
        _.groupBy(
          sortBoardsByTitleAndCreateTime(
            withPinyinBoards.slice(LAST_BOARD_NUM, withPinyinBoards.length)
          ),
          "_firstLetter"
        )
      )
        .sort((a, b) => `${a[0]}`.charCodeAt() - `${b[0]}`.charCodeAt())
        .reduce((result, [firstLetter, group]) => {
          result = result.concat(
            group.map((board, index) => {
              const item = { ...board };
              if (index === 0) {
                item._icon = firstLetter;
              }
              return item;
            })
          );
          return result;
        }, []);
      let groupByLetterBoards = [...lastBoards, ...otherBoards];
      if (
        !groupByLetterBoards?.length &&
        userBoardsReady &&
        emptyBoardsExpand
      ) {
        // 新用户画板数量为空时，展示下拉提示用户添加画板
        setOpen(true);
      }
      return {
        groupByLetterBoards,
        withPinyinBoards,
      };
    },
    {
      refreshDeps: [userBoards],
    }
  );
  useEffect(() => {
    setValue(_value || null);
  }, [_value]);

  useEffect(() => {
    if (selectFirst && boardData && !defaultSelectedRef.current) {
      const board =
        boardData?.groupByLetterBoards?.length > 0
          ? boardData?.groupByLetterBoards[0]
          : null;
      setValue(board?.board_id);
      onChange && onChange(board?.board_id, board);
      defaultSelectedRef.current = true;
    }
  }, [boardData, onChange, selectFirst]);

  const dataSource = useMemo(() => {
    if (!keyword) {
      return boardData?.groupByLetterBoards || [];
    }
    let boards = filterBoards(keyword, boardData);
    return boards.map((board, index) => {
      const item = { ...board };
      if (index === 0) {
        item._icon = "search";
      }

      return item;
    });
  }, [boardData, keyword]);

  const onDropdownVisibleChange = useCallback(async (open) => {
    // if (open && !boardData) {
    //   useAppStore.getState().refreshLastBoards();
    //   runGetBoards();
    // }
    setOpen(open);
  }, []);

  const onSearch = useCallback((keyword) => {
    setKeyword(keyword);
  }, []);

  const onSelect = useCallback(
    (board) => {
      setValue(board?.board_id);

      setOpen(false);
      onChange && onChange(board?.board_id, board);
    },
    [onChange]
  );

  const onCreate = useCallback(() => {
    menuRef.current?.scrollTo(0, 0);
  }, []);

  const onClear = useCallback(() => {
    setValue(null);
    onChange && onChange(null, null);
  }, [onChange]);

  const dropdownRender = useCallback(() => {
    return (
      <div className={styles.list}>
        <div className={styles.header}>输入名称，搜索画板</div>
        <If condition={boardLoading && !boardData}>
          <Then>
            <div className={styles.loading}>
              <HBLoading size="24px" />
            </div>
          </Then>
          <Else>
            <If condition={dataSource.length <= 0}>
              <Then>
                <div className={styles.empty}>
                  <div className={styles.img}>
                    <img
                      src="//cdn.dancf.com/fe-assets/img/1fdd546a6aaf6d46ca93fd467c674812.png"
                      alt="空画板"
                    />
                  </div>
                  <div className={styles.text}>
                    {onlyCreation ? "还没有【原创】画板哦～" : "还没有画板哦～"}
                  </div>
                </div>
              </Then>
              <Else>
                <BoardSelectBoardList
                  ref={menuRef}
                  dataSource={dataSource}
                  onSelect={onSelect}
                />
              </Else>
            </If>
          </Else>
        </If>
        <div className={styles.footer}>
          <div className={styles.split}></div>
          <CreateBoard
            onCreate={onCreate}
            useCreation={useCreation}
            boardName={keyword}
            onlyCreation={onlyCreation}
          />
        </div>
      </div>
    );
  }, [
    boardLoading,
    boardData,
    dataSource,
    onlyCreation,
    onSelect,
    onCreate,
    useCreation,
    keyword,
  ]);

  const options = useMemo(() => {
    const map = {};
    const options = [...dataSource, ...(appendBoardList || [])]
      .map((board) => ({
        label: board?.title,
        value: board?.board_id,
      }))
      .reduce((result, option) => {
        if (!map[option.value]) {
          result.push(option);
          map[option.value] = true;
        }
        return result;
      }, []);

    return options;
  }, [dataSource, appendBoardList]);

  useImperativeHandle(ref, () => {
    return {
      refresh: useAppStore.getState().refreshLastBoards,
    };
  });

  // 初始化时获取最新的画板，保证能拿到最新的画板列表数据
  useMount(() => {
    useAppStore.getState().refreshLastBoards();
  });

  return (
    <div
      className={classNames([
        styles.container,
        value || keyword ? styles.hasValue : null,
      ])}
    >
      <When condition={useLabel && placeholder}>
        <div className={styles.placeholder}>{placeholder}</div>
      </When>
      <Select
        className={styles.select}
        open={open}
        showSearch={true}
        suffixIcon={
          <HBIcon type="ic_arrow_down" className={styles.selectArrow} />
        }
        allowClear={true}
        value={value}
        options={options}
        dropdownRender={dropdownRender}
        dropdownClassName={styles.dropdown}
        onDropdownVisibleChange={onDropdownVisibleChange}
        onSearch={onSearch}
        onClear={onClear}
        placeholder={useLabel ? "" : placeholder}
        {...props}
      />
    </div>
  );
}

export default forwardRef(BoardSelect);
